import { useEffect, useMemo, useState, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '@app/hooks';
import { useAgentPermission } from '@entities/user';
import { useListContractsQuery, useUnarchiveContractMutation } from '@shared/api';
import { downloadCsv } from '@shared/lib/downloadCsv';
import { CONTRACT_TYPE_OPTIONS } from '@shared/lib/statusLabels';
import { useDebouncedValue } from '@shared/lib/useDebouncedValue';
import { Button } from '@shared/ui/Button';
import {
  KIND_COPY,
  PAGE_SIZE,
  SORT_OPTIONS,
  STATUS_OPTIONS,
} from './constants';
import { ContractsPagination } from './ContractsPagination';
import { ContractsTable } from './ContractsTable';
import { CreateContractModal } from './CreateContractModal';
import {
  Bar,
  ContractsBarRight,
  ExportCsvButton,
  FilterSelect,
  Note,
  ResetSearchButton,
  SearchBox,
  Tab,
  Tabs,
  Toolbar,
} from './styles';
import type { ContractsProps } from './types';

export function Contracts({ kind = 'contract' }: ContractsProps) {
  const navigate = useNavigate();
  const { can_write } = useAgentPermission('statut');
  const copy = KIND_COPY[kind];
  const basePath = copy.basePath;

  const [searchInput, setSearchInput] = useState('');
  const deferredSearch = useDebouncedValue(searchInput.trim(), 300);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [sort, setSort] = useState('created_at');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);

  const [view, setView] = useState<'active' | 'archive'>('active');
  const isArchive = kind === 'precontract' && view === 'archive';

  useEffect(() => {
    setPage(0);
  }, [deferredSearch, statusFilter, typeFilter, sort, order, kind, view]);

  const queryArg = useMemo(
    () => ({
      kind,
      search: deferredSearch || undefined,
      status: statusFilter || undefined,
      contract_type: typeFilter || undefined,
      archived: isArchive || undefined,
      sort,
      order,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [kind, deferredSearch, statusFilter, typeFilter, isArchive, sort, order, page],
  );

  const { data, isLoading, isFetching } = useListContractsQuery(queryArg, {
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const accessToken = useAppSelector((s) => s.auth.accessToken);
  const [isExporting, setIsExporting] = useState(false);
  async function onExport() {
    setIsExporting(true);
    try {
      await downloadCsv(
        '/agents/statut/contracts/export.csv',
        {
          kind,
          search: deferredSearch,
          status: statusFilter,
          contract_type: typeFilter,
          sort,
          order,
        },
        accessToken,
        `${basePath}.csv`,
      );
    } finally {
      setIsExporting(false);
    }
  }

  const [unarchiveContract, { isLoading: isRestoring }] = useUnarchiveContractMutation();

  async function onRestore(e: MouseEvent, id: string) {
    e.stopPropagation();
    try {
      await unarchiveContract(id).unwrap();
    } catch {
      // Инвалидация тега вернёт список в согласованное состояние; молчим.
    }
  }

  const [open, setOpen] = useState(false);
  const items = data?.items ?? [];

  function toggleSort(field: string) {
    if (sort === field) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(field);
      setOrder('desc');
    }
  }

  const emptyMessage = isArchive
    ? 'Архив пуст — сюда попадают заключённые и брошенные преддоговоры.'
    : deferredSearch || statusFilter || typeFilter
      ? 'По заданным фильтрам ничего не найдено. Попробуйте сбросить условия.'
      : copy.emptyDefault;

  return (
    <>
      {kind === 'precontract' && (
        <Tabs role="tablist">
          <Tab type="button" $active={!isArchive} onClick={() => setView('active')}>
            В работе
          </Tab>
          <Tab type="button" $active={isArchive} onClick={() => setView('archive')}>
            Архив
          </Tab>
        </Tabs>
      )}

      <Note>
        {isArchive
          ? 'Архив преддоговорной работы. Заключённые преддоговоры хранятся здесь как досье переговоров (удаляются через 3 года после окончания договора); брошенные — те, которых никто не трогал 30 дней, — удаляются через 30 дней в архиве. «Вернуть» возвращает преддоговор в работу.'
          : copy.intro}
      </Note>

      <Toolbar>
        <SearchBox
          placeholder="Поиск: номер, контрагент, предмет…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <FilterSelect
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          title="Фильтр по статусу"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label === 'Любой' ? 'Все статусы' : o.label}
            </option>
          ))}
        </FilterSelect>
        <FilterSelect
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          title="Фильтр по типу"
        >
          <option value="">Все типы</option>
          {CONTRACT_TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </FilterSelect>
        <FilterSelect value={sort} onChange={(e) => setSort(e.target.value)} title="Сортировка">
          {SORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </FilterSelect>
        {!isArchive && (
          <Button
            onClick={() => setOpen(true)}
            disabled={!can_write}
            title={!can_write ? 'Только чтение' : undefined}
          >
            {copy.newButton}
          </Button>
        )}
      </Toolbar>

      <Bar>
        <span>
          {isLoading ? 'Загрузка…' : `Найдено: ${total}${isFetching ? ' · обновление…' : ''}`}
        </span>
        <ContractsBarRight>
          {deferredSearch && (
            <span>
              По запросу «<strong>{deferredSearch}</strong>»{' · '}
              <ResetSearchButton
                type="button"
                onClick={() => setSearchInput('')}
              >
                сбросить
              </ResetSearchButton>
            </span>
          )}
          <ExportCsvButton
            type="button"
            onClick={onExport}
            disabled={isExporting || total === 0}
            title="Скачать текущую выборку в CSV для Excel"
          >
            {isExporting ? 'Готовим…' : 'Экспорт CSV'}
          </ExportCsvButton>
        </ContractsBarRight>
      </Bar>

      <ContractsTable
        items={items}
        isLoading={isLoading}
        isArchive={isArchive}
        sort={sort}
        order={order}
        canWrite={can_write}
        isRestoring={isRestoring}
        emptyMessage={emptyMessage}
        onSort={toggleSort}
        onRowClick={(id) => navigate(`/agents/statut/${basePath}/${id}`)}
        onRestore={onRestore}
      />

      {total > PAGE_SIZE && (
        <ContractsPagination
          page={page}
          totalPages={totalPages}
          total={total}
          itemCount={items.length}
          onPageChange={setPage}
        />
      )}

      <CreateContractModal
        open={open}
        kind={kind}
        title={copy.modalTitle}
        canWrite={can_write}
        onClose={() => setOpen(false)}
      />
    </>
  );
}
